'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import type { ReactNode } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Loader2, Film, Music, Image, Trash2, AlertTriangle } from 'lucide-react';
import type { MediaAsset, UploadProgress, UploadFn, PollAssetFn } from './media-types';

type AcceptType = 'image' | 'audio' | 'video' | 'pdf' | 'all';

const ACCEPT_MAP: Record<AcceptType, Record<string, string[]>> = {
  image: { 'image/jpeg': [], 'image/png': [], 'image/webp': [], 'image/gif': [] },
  audio: { 'audio/mpeg': [], 'audio/mp4': [], 'audio/ogg': [], 'audio/wav': [] },
  video: { 'video/mp4': [], 'video/webm': [], 'video/quicktime': [] },
  pdf: { 'application/pdf': [] },
  all: { 'image/*': [], 'audio/*': [], 'video/*': [] },
};

const TYPE_ICON: Record<string, ReactNode> = {
  image: <Image size={16} />,
  audio: <Music size={16} />,
  video: <Film size={16} />,
};

const LARGE_FILE_BYTES = 200 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function formatSpeed(bps: number): string {
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bps >= 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${bps.toFixed(0)} B/s`;
}

function formatEta(secs: number): string {
  if (!isFinite(secs) || secs <= 0) return '';
  if (secs >= 3600) return `~${Math.round(secs / 3600)} jam`;
  if (secs >= 60) return `~${Math.round(secs / 60)} mnt`;
  return `~${Math.round(secs)} dtk`;
}

interface UploadEntry {
  file: File;
  state: 'uploading' | 'done' | 'error';
  percent: number;
  speedBps: number;
  etaSecs: number;
  error?: string;
  asset?: MediaAsset;
  abortController?: AbortController;
  /** e.g. "Dioptimalkan dari 2.8 MB ke 180 KB" — set once by a compression note,
   * then carried forward across subsequent progress ticks and into 'done'. */
  note?: string;
}

interface MediaUploaderProps {
  accept?: AcceptType;
  multiple?: boolean;
  onUploaded: (asset: MediaAsset) => void;
  uploadFile: UploadFn;
  pollAsset?: PollAssetFn;
  label?: string;
  hint?: string;
}

export function MediaUploader({
  accept = 'all',
  multiple = false,
  onUploaded,
  uploadFile,
  pollAsset,
  label,
  hint,
}: MediaUploaderProps) {
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const pollIntervalsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());
  useEffect(() => {
    return () => { pollIntervalsRef.current.forEach(clearInterval); };
  }, []);

  const processFile = useCallback(async (file: File) => {
    const abortController = new AbortController();
    const entry: UploadEntry = {
      file,
      state: 'uploading',
      percent: 0,
      speedBps: 0,
      etaSecs: 0,
      abortController,
    };
    setEntries((prev) => [...prev, entry]);

    try {
      const asset = await uploadFile(
        file,
        (progress: UploadProgress) => {
          setEntries((prev) =>
            prev.map((e) =>
              e.file === file
                ? {
                    ...e,
                    percent: progress.percent,
                    speedBps: progress.speedBps,
                    etaSecs: progress.etaSecs,
                    ...(progress.note !== undefined && { note: progress.note }),
                  }
                : e
            )
          );
        },
        abortController.signal,
      );

      setEntries((prev) =>
        prev.map((e) =>
          e.file === file ? { ...e, state: 'done', asset, percent: 100 } : e
        )
      );
      onUploaded(asset);

      if (pollAsset && asset.provider === 'cloudflare-stream' && asset.status === 'PROCESSING') {
        const MAX_POLLS = 60;
        let polls = 0;
        const interval = setInterval(async () => {
          polls++;
          try {
            const updated = await pollAsset(asset.id);
            if (updated.status !== 'PROCESSING') {
              clearInterval(interval);
              pollIntervalsRef.current.delete(interval);
              setEntries((prev) =>
                prev.map((e) => (e.file === file ? { ...e, asset: updated } : e))
              );
              onUploaded(updated);
            }
          } catch { /* ignore */ }
          if (polls >= MAX_POLLS) {
            clearInterval(interval);
            pollIntervalsRef.current.delete(interval);
          }
        }, 5000);
        pollIntervalsRef.current.add(interval);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setEntries((prev) => prev.filter((e) => e.file !== file));
        return;
      }
      const message = err instanceof Error ? err.message : 'Upload gagal';
      setEntries((prev) =>
        prev.map((e) =>
          e.file === file ? { ...e, state: 'error', error: message } : e
        )
      );
    }
  }, [onUploaded, uploadFile, pollAsset]);

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      accepted.forEach(processFile);
      rejected.forEach((r) => {
        const errorMsg = r.errors.map((e) => e.message).join(', ');
        setEntries((prev) => [
          ...prev,
          { file: r.file, state: 'error', percent: 0, speedBps: 0, etaSecs: 0, error: errorMsg },
        ]);
      });
    },
    [processFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT_MAP[accept],
    multiple,
  });

  const cancelUpload = (index: number) => {
    entries[index]?.abortController?.abort();
  };

  const removeEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full space-y-2">
      {label && <p className="text-sm font-medium text-gray-700">{label}</p>}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors select-none
          ${isDragActive
            ? 'border-teal-400 bg-teal-50'
            : 'border-gray-300 hover:border-teal-400 bg-gray-50 hover:bg-gray-100'
          }`}
      >
        <input {...getInputProps()} />
        <Upload size={28} className={`mx-auto mb-2 ${isDragActive ? 'text-teal-500' : 'text-gray-400'}`} />
        <p className="text-sm font-medium text-gray-600">
          {isDragActive ? 'Lepaskan file di sini' : multiple ? 'Klik atau seret beberapa file' : 'Klik atau seret file'}
        </p>
        {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
      </div>
      {entries.length > 0 && (
        <div className="space-y-2 mt-2">
          {entries.map((entry, i) => (
            <UploadEntryRow
              key={i}
              entry={entry}
              onCancel={() => cancelUpload(i)}
              onRemove={() => removeEntry(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UploadEntryRow({
  entry,
  onCancel,
  onRemove,
}: {
  entry: UploadEntry;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const fileType = entry.asset?.type?.toLowerCase() as keyof typeof TYPE_ICON | undefined;
  const icon = fileType && TYPE_ICON[fileType];
  const isLarge = entry.file.size >= LARGE_FILE_BYTES;

  if (entry.state === 'uploading') {
    return (
      <div className="border rounded-lg px-3 py-2 bg-gray-50 space-y-1.5">
        {isLarge && (
          <div className="flex items-center gap-1.5 text-amber-600">
            <AlertTriangle size={12} className="flex-shrink-0" />
            <span className="text-xs">File besar ({formatBytes(entry.file.size)}) — pastikan koneksi stabil</span>
          </div>
        )}
        {entry.note && <p className="text-xs text-teal-600">{entry.note}</p>}
        <div className="flex items-center gap-2">
          <Loader2 size={16} className="text-teal-500 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-gray-600 truncate">{entry.file.name}</p>
              {!isLarge && (
                <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(entry.file.size)}</span>
              )}
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-teal-500 transition-all duration-300"
                style={{ width: `${entry.percent}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-0.5">
              <span className="text-xs text-gray-400">
                {entry.speedBps > 0 ? formatSpeed(entry.speedBps) : 'Menghubungkan...'}
              </span>
              <span className="text-xs text-gray-400">
                {entry.percent > 0 && entry.etaSecs > 1
                  ? `${entry.percent}% · ${formatEta(entry.etaSecs)}`
                  : `${entry.percent}%`}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex-shrink-0"
            title="Batalkan upload"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (entry.state === 'done') {
    return (
      <div className="border border-green-200 rounded-lg px-3 py-2 bg-green-50 flex items-center gap-2">
        <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
        {icon && <span className="text-gray-500 flex-shrink-0">{icon}</span>}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-green-700 truncate">
            {entry.asset?.status === 'PROCESSING' ? 'Memproses video...' : entry.file.name}
          </p>
          {entry.asset?.status === 'PROCESSING' && (
            <p className="text-xs text-green-600">Video akan siap dalam beberapa menit</p>
          )}
          {entry.note && <p className="text-xs text-green-700 truncate">{entry.note}</p>}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 rounded hover:bg-green-100 text-green-600 flex-shrink-0"
          title="Hapus"
        >
          <Trash2 size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="border border-red-200 rounded-lg px-3 py-2 bg-red-50 flex items-center gap-2">
      <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-600 truncate">{entry.file.name}</p>
        <p className="text-xs text-red-600">{entry.error}</p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded hover:bg-red-100 text-red-600 flex-shrink-0"
        title="Hapus"
      >
        <X size={14} />
      </button>
    </div>
  );
}
